/**
 * BottomNav -- 5-tab bottom navigation bar (56px).
 * Tabs 0-3: from MODULE_REGISTRY modules with navPosition.
 * Tab 4: "More" button that opens a popup menu with additional modules.
 * Active tab highlighted with brand color. RTL-safe. Haptic on tap.
 */

import type { AweniContext } from '../context/AweniContext';
import { getMoreModules } from '../modules/registry';

interface NavModule {
  id: string;
  icon: string;
  label: Record<string, string>;
  color: string;
  navPosition?: number;
}

// Filled icon variants for active state
const FILLED_ICONS: Record<string, string> = {
  home: '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><rect x="9" y="12" width="6" height="10" fill="var(--bg-primary)"/></svg>',
  games: '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="2" y="6" width="20" height="12" rx="2"/><line x1="6" y1="12" x2="10" y2="12" stroke="var(--bg-primary)" stroke-width="2"/><line x1="8" y1="10" x2="8" y2="14" stroke="var(--bg-primary)" stroke-width="2"/><circle cx="15" cy="10" r="1.2" fill="var(--bg-primary)"/><circle cx="18" cy="13" r="1.2" fill="var(--bg-primary)"/></svg>',
  memoriz: '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7z"/></svg>',
  balancetab: '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="2" y="4" width="20" height="16" rx="2"/><rect x="2" y="10" width="20" height="2" fill="var(--bg-primary)"/></svg>',
  more: '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/></svg>',
};

const MORE_ICON_OUTLINE = '<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/></svg>';

// IDs of modules that live inside the "More" menu
const MORE_MODULE_IDS = ['selecto', 'blog', 'chat', 'community', 'settings'];

let _container: HTMLElement | null = null;
let _ctx: AweniContext | null = null;
let _activeId = 'home';
let _buttons: Map<string, HTMLButtonElement> = new Map();
let _modules: NavModule[] = [];
let _onNavigate: ((moduleId: string) => void) | null = null;
let _moreMenuEl: HTMLElement | null = null;
let _backdropEl: HTMLElement | null = null;
let _moreMenuOpen = false;

/**
 * Mount the bottom navigation bar with the given modules.
 */
export function mountBottomNav(
  container: HTMLElement,
  ctx: AweniContext,
  modules: NavModule[],
  onNavigate: (moduleId: string) => void,
): void {
  _ctx = ctx;
  _container = container;
  _modules = modules;
  _onNavigate = onNavigate;
  _buttons.clear();

  container.setAttribute('role', 'tablist');
  container.setAttribute('aria-label', 'Main navigation');
  container.style.cssText = `
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: 56px;
    z-index: 100;
    background: var(--bottomnav-bg);
    border-top: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding-bottom: env(safe-area-inset-bottom, 0px);
    box-shadow: 0 -1px 3px rgba(0,0,0,0.05);
  `;

  const locale = (ctx.i18n.locale || 'en') as string;

  // Render the 4 main nav tabs
  for (const mod of modules) {
    const btn = _createTabButton(mod, locale);
    btn.addEventListener('click', () => {
      if (navigator.vibrate) navigator.vibrate(10);
      _closeMoreMenu();
      setActiveTab(mod.id);
      onNavigate(mod.id);
    });
    _buttons.set(mod.id, btn);
    container.appendChild(btn);
  }

  // Render the "More" tab
  const moreLabel: Record<string, string> = {
    en: 'More', fr: 'Plus', ar: '\u0627\u0644\u0645\u0632\u064A\u062F', es: 'M\u00e1s', de: 'Mehr',
  };
  const moreLabelText = moreLabel[locale] || moreLabel['en'];
  const moreBtn = document.createElement('button');
  moreBtn.setAttribute('role', 'tab');
  moreBtn.setAttribute('data-module', 'more');
  moreBtn.setAttribute('aria-label', moreLabelText);
  moreBtn.setAttribute('aria-selected', 'false');
  moreBtn.setAttribute('aria-haspopup', 'true');
  moreBtn.setAttribute('aria-expanded', 'false');
  moreBtn.style.cssText = `
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    height: 100%;
    background: transparent;
    border: none;
    padding: 4px 0;
    min-width: 0;
    position: relative;
    color: var(--text-secondary);
    cursor: pointer;
  `;

  const moreIconWrap = document.createElement('span');
  moreIconWrap.className = 'tab-icon';
  moreIconWrap.style.cssText = 'display:flex;align-items:center;justify-content:center;height:24px;width:24px;';
  moreIconWrap.innerHTML = MORE_ICON_OUTLINE;

  const moreLabelEl = document.createElement('span');
  moreLabelEl.className = 'tab-label';
  moreLabelEl.style.cssText = 'font-size:10px;font-weight:500;line-height:1;white-space:nowrap;';
  moreLabelEl.textContent = moreLabelText;

  moreBtn.append(moreIconWrap, moreLabelEl);
  moreBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (navigator.vibrate) navigator.vibrate(10);
    _toggleMoreMenu();
  });
  _buttons.set('more', moreBtn);
  container.appendChild(moreBtn);

  // Build the More menu popup (initially hidden)
  _buildMoreMenu(locale);

  // Set initial active
  setActiveTab(_activeId);
}

/**
 * Create a tab button for a nav module.
 */
function _createTabButton(mod: NavModule, locale: string): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.setAttribute('role', 'tab');
  btn.setAttribute('data-module', mod.id);
  const localLabel = mod.label[locale] || mod.label['en'] || mod.id;
  btn.setAttribute('aria-label', localLabel);
  btn.setAttribute('aria-selected', 'false');
  btn.style.cssText = `
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    height: 100%;
    background: transparent;
    border: none;
    padding: 4px 0;
    min-width: 0;
    position: relative;
    color: var(--text-secondary);
    cursor: pointer;
  `;

  const iconWrap = document.createElement('span');
  iconWrap.className = 'tab-icon';
  iconWrap.style.cssText = 'display:flex;align-items:center;justify-content:center;height:24px;width:24px;';
  const iconSvg = mod.icon.replace('<svg', '<svg width="22" height="22"');
  iconWrap.innerHTML = iconSvg;

  const label = document.createElement('span');
  label.className = 'tab-label';
  label.style.cssText = 'font-size:10px;font-weight:500;line-height:1;white-space:nowrap;';
  label.textContent = localLabel;

  btn.append(iconWrap, label);
  return btn;
}

/**
 * Build the "More" popup menu.
 */
function _buildMoreMenu(locale: string): void {
  // Backdrop
  const backdrop = document.createElement('div');
  backdrop.style.cssText = `
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.3);
    z-index: 99;
    display: none;
    opacity: 0;
    transition: opacity 0.2s ease;
  `;
  backdrop.addEventListener('click', () => _closeMoreMenu());
  document.body.appendChild(backdrop);
  _backdropEl = backdrop;

  // Menu panel
  const menu = document.createElement('div');
  menu.setAttribute('role', 'menu');
  menu.setAttribute('aria-label', 'More options');
  menu.style.cssText = `
    position: fixed;
    bottom: 56px;
    left: 8px;
    right: 8px;
    z-index: 101;
    background: var(--card-bg, #fff);
    border: 1px solid var(--border);
    border-radius: 16px 16px 0 0;
    box-shadow: 0 -4px 24px rgba(0,0,0,0.12);
    padding: 16px 12px 12px;
    display: none;
    transform: translateY(100%);
    transition: transform 0.25s ease, opacity 0.2s ease;
    opacity: 0;
  `;

  // Handle — visual drag indicator
  const handle = document.createElement('div');
  handle.style.cssText = 'width:36px;height:4px;border-radius:2px;background:var(--border);margin:0 auto 12px;';
  menu.appendChild(handle);

  // Get modules for the More menu from the registry
  const moreModules = getMoreModules();

  for (const mod of moreModules) {
    const localLabel = (mod.label as Record<string, string>)[locale] || (mod.label as Record<string, string>)['en'] || mod.id;
    const item = document.createElement('button');
    item.setAttribute('role', 'menuitem');
    item.style.cssText = `
      display: flex;
      align-items: center;
      gap: 12px;
      width: 100%;
      padding: 12px;
      border: none;
      border-radius: 12px;
      background: transparent;
      color: var(--text-primary);
      font-size: 15px;
      font-weight: 500;
      cursor: pointer;
      text-align: start;
      transition: background 0.15s ease;
    `;
    item.addEventListener('mouseenter', () => { item.style.background = 'var(--hover-bg, rgba(0,0,0,0.05))'; });
    item.addEventListener('mouseleave', () => { item.style.background = 'transparent'; });

    // Icon
    const iconEl = document.createElement('span');
    iconEl.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 10px;
      background: ${mod.color}15;
      color: ${mod.color};
      flex-shrink: 0;
    `;
    const iconSvg = mod.icon.replace('<svg', '<svg width="20" height="20"');
    iconEl.innerHTML = iconSvg;

    // Label
    const labelEl = document.createElement('span');
    labelEl.textContent = localLabel;

    item.append(iconEl, labelEl);

    item.addEventListener('click', () => {
      _closeMoreMenu();
      // Navigate to #/more/<moduleId>
      if (_onNavigate) {
        setActiveTab('more');
        _onNavigate(`more/${mod.id}`);
      }
    });

    menu.appendChild(item);
  }

  document.body.appendChild(menu);
  _moreMenuEl = menu;
}

function _toggleMoreMenu(): void {
  if (_moreMenuOpen) {
    _closeMoreMenu();
  } else {
    _openMoreMenu();
  }
}

function _openMoreMenu(): void {
  if (!_moreMenuEl || !_backdropEl) return;
  _moreMenuOpen = true;

  _backdropEl.style.display = 'block';
  _moreMenuEl.style.display = 'block';

  // Trigger animation on next frame
  requestAnimationFrame(() => {
    if (_backdropEl) _backdropEl.style.opacity = '1';
    if (_moreMenuEl) {
      _moreMenuEl.style.transform = 'translateY(0)';
      _moreMenuEl.style.opacity = '1';
    }
  });

  const moreBtn = _buttons.get('more');
  if (moreBtn) {
    moreBtn.setAttribute('aria-expanded', 'true');
  }
}

function _closeMoreMenu(): void {
  if (!_moreMenuEl || !_backdropEl) return;
  _moreMenuOpen = false;

  _backdropEl.style.opacity = '0';
  _moreMenuEl.style.transform = 'translateY(100%)';
  _moreMenuEl.style.opacity = '0';

  setTimeout(() => {
    if (_backdropEl) _backdropEl.style.display = 'none';
    if (_moreMenuEl) _moreMenuEl.style.display = 'none';
  }, 250);

  const moreBtn = _buttons.get('more');
  if (moreBtn) {
    moreBtn.setAttribute('aria-expanded', 'false');
  }
}

/**
 * Set the active tab by module ID.
 * If the moduleId belongs to a "More" sub-module, highlight the "More" tab.
 */
export function setActiveTab(moduleId: string): void {
  _activeId = moduleId;
  const locale = (_ctx?.i18n.locale || 'en') as string;

  // Determine if this module is in the "More" group
  const isMoreModule = MORE_MODULE_IDS.includes(moduleId);
  const highlightId = isMoreModule ? 'more' : moduleId;

  // Update main nav tabs
  for (const mod of _modules) {
    const btn = _buttons.get(mod.id);
    if (!btn) continue;
    const isActive = mod.id === highlightId;

    btn.setAttribute('aria-selected', String(isActive));

    const iconWrap = btn.querySelector('.tab-icon') as HTMLSpanElement;
    const label = btn.querySelector('.tab-label') as HTMLSpanElement;

    if (iconWrap) {
      if (isActive && FILLED_ICONS[mod.id]) {
        iconWrap.innerHTML = FILLED_ICONS[mod.id];
      } else {
        const iconSvg = mod.icon.replace('<svg', '<svg width="22" height="22"');
        iconWrap.innerHTML = iconSvg;
      }
    }

    const color = isActive ? 'var(--accent)' : 'var(--text-secondary)';
    btn.style.color = color;
    if (label) {
      label.style.fontWeight = isActive ? '600' : '500';
    }
  }

  // Update "More" tab
  const moreBtn = _buttons.get('more');
  if (moreBtn) {
    const isActive = highlightId === 'more';
    moreBtn.setAttribute('aria-selected', String(isActive));
    const moreIcon = moreBtn.querySelector('.tab-icon') as HTMLSpanElement;
    const moreLabel = moreBtn.querySelector('.tab-label') as HTMLSpanElement;
    if (moreIcon) {
      moreIcon.innerHTML = isActive ? FILLED_ICONS['more'] : MORE_ICON_OUTLINE;
    }
    moreBtn.style.color = isActive ? 'var(--accent)' : 'var(--text-secondary)';
    if (moreLabel) {
      moreLabel.style.fontWeight = isActive ? '600' : '500';
    }
  }
}
